import {
  Box,
  Checkbox,
  Link,
  VStack,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { loginSchema } from '../../../utils/schema'
import ConfirmButton from '../components/ConfirmButton'
import ControlInput from '../components/ControlInput'
import useAuthAPI from '../../../api/api_hooks/auth/useAuthAPI'
import { Navigate, useSearchParams } from 'react-router-dom'
import ControlPassword from '../components/ControlPassword'
import { useTranslation } from 'react-i18next'
import LanguageButton from '../../_shared/LanguageButton'
import { useMyProfile } from '../../../api/api_hooks/user/myProfile'
import { useEffect } from 'react'

type Inputs = {
  email: string
  password: string
  keepMeLogged?: boolean
}

export default function LoginForm() {
  const toast = useToast()
  const { useLogin } = useAuthAPI()
  const post = useLogin()
  const { t } = useTranslation()
  const { data: user, isLoading: isLoadingUser } = useMyProfile()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: yupResolver(loginSchema),
  })

  const formBg = useColorModeValue('white', 'gray.800')
  const formBorder = useColorModeValue('gray.100', 'gray.700')
  const linkColor = useColorModeValue('purple.600', 'purple.300')
  const linkHoverBg = useColorModeValue('purple.50', 'purple.900')
  const checkboxBorder = useColorModeValue('#D0D5DD', 'gray.600')
  const titleColor = useColorModeValue('gray.700', 'gray.200')
  const subTitleColor = useColorModeValue('gray.600', 'gray.300')

  // Where to go after login: a `redirect` set by the auth guard (e.g. a case link
  // from a reminder email). Only same-origin app paths are honoured — never an
  // external URL (open-redirect guard) and never back to /login.
  const [searchParams] = useSearchParams()
  const redirectParam = searchParams.get('redirect')
  const safeRedirect =
    redirectParam &&
    redirectParam.startsWith('/') &&
    !redirectParam.startsWith('//') &&
    !redirectParam.startsWith('/login')
      ? redirectParam
      : null

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    post.mutateAsync(data as unknown as void).catch(() =>
      toast({
        title: t('auth.error.passwordOrEmailInvalid'),
        position: 'top',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    )
  }

  useEffect(() => {
    if (post.isSuccess && user && !isLoadingUser) {
      const localPlan = localStorage.getItem('userPlan')
      if (!user.plan && localPlan) {
        localStorage.removeItem('userPlan')
        localStorage.removeItem('userModules')
      }
    }
  }, [post.isSuccess, user, isLoadingUser])

  if (post.isSuccess && user && !isLoadingUser) {
    if (!user.plan) return <Navigate to="/select-plan" replace />
    return <Navigate to={safeRedirect || '/home'} replace />
  }

  return (
    <Box
      w="100%"
      maxW={420}
      mx="auto"
    >
      <VStack spacing={4} w="100%">
        <Box textAlign="center" w="100%">
          <Box
          fontSize={{ base: 'xl', md: '2xl' }}
          fontWeight="600"
          color={titleColor}
          mb={4}
        >
          {t('auth.welcomeCareflai')}
        </Box>
          <Box
            fontSize={{ base: 'md', md: 'lg' }}
            color={subTitleColor}
          >
            {t('auth.subTitle')}
          </Box>
        </Box>

        <Box
          w="100%"
          bg={formBg}
          p={{ base: 5, md: 6 }}
          borderRadius="xl"
          boxShadow="xl"
          borderWidth="1px"
          borderColor={formBorder}
          transition="all 0.3s"
          _hover={{
            boxShadow: '2xl',
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={3} mb={4}>
              <ControlInput
                register={{ ...register('email') }}
                label={t('shared.auth.emailLabel')}
                placeholder={t('shared.auth.emailPlaceholder')}
                isInvalid={!!errors.email}
                errorMessage={errors.email && errors.email.message}
              />
              <ControlPassword
                register={{ ...register('password') }}
                label={t('shared.auth.passwordLabel')}
                placeholder={t('shared.auth.passwordPlaceholder')}
                isInvalid={!!errors.password}
                errorMessage={errors.password && errors.password.message}
              />
              <Box w="100%">
                <Checkbox
                  borderRadius={5}
                  borderColor={checkboxBorder}
                  size="sm"
                  colorScheme="purple"
                  {...register('keepMeLogged')}
                >
                  {t('auth.rememberMe')}
                </Checkbox>
              </Box>
            </VStack>

            <ConfirmButton />
          </form>
        </Box>

        <VStack spacing={1} w="100%">
          <Link
            href="#"
            color={linkColor}
            fontWeight={500}
            fontSize="sm"
            px={2}
            py={1}
            borderRadius="md"
            transition="all 0.2s"
            _hover={{
              bg: linkHoverBg,
              textDecoration: 'none',
            }}
          >
            {t('auth.forgotPassword')}
          </Link>
          {/* Sign-up link hidden — access is invitation-only for now. Restore this
              block (and the `auth.noAccount` / `auth.register` copy) when public
              registration opens. */}
        </VStack>

        <Box display="flex" justifyContent="center">
          <LanguageButton color="brand.purple" />
        </Box>
      </VStack>
    </Box>
  )
}
